/**
 * Herói dos cartões do dia em `/revisao`.
 * The total is the shared reading (`leitura.total`).
 */
import { Flame, GraduationCap, Layers, Sparkles, type LucideIcon } from "lucide-react";
import type { RevisaoDiaLeitura } from "./revisaoHome";

export interface RevisaoHeroProps {
  leitura: RevisaoDiaLeitura;
  /** A primeira leitura ainda não chegou. Zero aqui não é "nada a estudar". */
  aguardando?: boolean;
}

const ESTADOS: {
  key: keyof RevisaoDiaLeitura["counts"];
  label: string;
  tile: string;
  Icon: LucideIcon;
}[] = [
  {
    key: "new",
    label: "Novos",
    tile: "bg-accent-purple/[0.12] text-accent-purple-ink",
    Icon: Sparkles,
  },
  {
    key: "learning",
    label: "Aprendendo",
    tile: "bg-accent-orange/[0.12] text-accent-orange-ink",
    Icon: GraduationCap,
  },
  {
    key: "review",
    label: "Revisão",
    tile: "bg-accent-green/[0.12] text-accent-green-ink",
    Icon: Layers,
  },
];

function textoSelecao(total: number, aguardando: boolean): string {
  if (aguardando) return "Carregando a revisão…";
  if (total === 0) return "Nenhum cartão nesta seleção.";
  return "nesta seleção";
}

/** `null` é indisponível ("—"), nunca um zero inventado. */
function textoSequencia(streak: number | null): string {
  if (streak === null) return "—";
  return streak === 1 ? "1 dia" : `${streak} dias`;
}

export default function RevisaoHero({
  leitura,
  aguardando = false,
}: RevisaoHeroProps): JSX.Element {
  const sequencia = textoSequencia(leitura.streak);

  return (
    <section className="rounded-card border border-edge bg-surface p-4 sm:p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-ink">Cartões de hoje</h2>
          {/* The total is the shared reading. */}
          <p className="mt-1 text-4xl font-semibold tabular-nums text-ink">{leitura.total}</p>
          <p className="mt-1 text-sm text-ink-secondary">{textoSelecao(leitura.total, aguardando)}</p>
        </div>

        <div className="flex items-center gap-3 md:shrink-0">
          <span
            aria-hidden
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-chip bg-accent-cyan/[0.12] text-accent-cyan-ink"
          >
            <Flame size={20} strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-medium text-ink-muted">Sequência</p>
            <p
              className="text-xl font-semibold tabular-nums text-ink sm:text-2xl"
              aria-label={leitura.streak === null ? "indisponível" : undefined}
            >
              {sequencia}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 sm:mt-6 sm:gap-3">
        {ESTADOS.map(({ key, label, tile, Icon }) => (
          <div key={key} className="min-w-0 rounded-chip border border-edge p-2 sm:p-3">
            <span
              aria-hidden
              className={`inline-flex h-7 w-7 items-center justify-center rounded-chip sm:h-8 sm:w-8 ${tile}`}
            >
              <Icon size={16} strokeWidth={2} />
            </span>
            <p className="mt-2 text-lg font-semibold tabular-nums leading-none text-ink sm:text-2xl">
              {leitura.counts[key]}
            </p>
            <p className="mt-1 text-xs leading-tight text-ink-secondary">{label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
